import designTokens from '@customink/design-tokens/build/js/designTokens';
import { Alert } from '@customink/pigment-react';
import type { AlertProps } from '@mui/material/Alert';
import { useEffect, useMemo, useState } from 'react';

import { avalaraApiUrl } from './EmbeddedTaxExemptionDialog';
import { ErrorLoadingAvalaraAlert } from './ErrorLoadingAvalaraAlert';
import type { GenCertCertificate } from './genCert';

import { rollbar } from '@/config/rollbar.config';
import { useFeatureFlagEnabled } from '@/hooks/FeatureFlags';

type AvalaraEcmPluginProps = {
  token: string;
  /** US state or territory to get the cert for, full name */
  state: string;
  onClose: () => void;
  onSuccess: (certificate: GenCertCertificate) => void;
};

const avalaraIframeStyles = `
            button {
                border-radius: 6px !important;
            }
            button:focus {
                box-shadow: none !important;
            }

            button.primary {
                color: white !important;
                background-color: ${designTokens.color.secondary.default.value} !important;
            }
            button.primary:hover {
                background-color: ${designTokens.color.secondary.hover.value} !important;
            }
            button.primary:active {
                background-color: ${designTokens.color.secondary.active.value} !important;
            }

            button.tertiary, button.secondary {
                color: ${designTokens.color.secondary.default.value} !important;
                background-color: white !important;
                border: 1px solid ${designTokens.color.secondary.default.value} !important;
            }
            button.tertiary:hover, button.secondary:hover {
                color: white !important;
                background-color: ${designTokens.color.secondary.hover.value} !important;
            }
            button.tertiary:active, button.secondary:active {
                color: white !important;
                background-color: ${designTokens.color.secondary.active.value} !important;
            }

            button.link {
                color: ${designTokens.color.secondary.default.value} !important;
            }

            button.loading {
                background-color: rgba(0, 0, 0, 0.12) !important;
                color: rgba(0, 0, 0, 0.26) !important;
            }
            input:focus {
                box-shadow: none !important;
                border: 1px solid ${designTokens.color.secondary.default.value} !important;
            }
            button s-icon {
                vertical-align: text-top;
            }
        `;

const applyCustomStyles = (formContainer: HTMLDivElement) => {
  const iframe = formContainer?.firstElementChild;
  if (!iframe) {
    rollbar.warn('avalara custom styles: iframe is null');
    return false;
  }
  const window = (iframe as HTMLIFrameElement).contentWindow;
  if (!window) {
    rollbar.warn('avalara custom styles: window is null');
    return false;
  }
  const { document } = window;
  if (!document) {
    rollbar.warn('avalara custom styles: document is null');
    return false;
  }

  const style = document.createElement('style');
  style.innerHTML = avalaraIframeStyles;
  document.head.appendChild(style);
  return true;
};

/**
 * Context:
 * Avalara script relies on 3 levels of indirection: the first script loads another script etc.
 * The `GenCert.init` does following, among other things:
 *    ```
 *    // loads  "/html/gencert/index.html";
 *    document.getElementById('gencert_iframe').contentWindow.document.write(xmlhttp.responseText);
 *    document.getElementById('gencert_iframe').contentWindow.document.close();
 *    ```
 * Virtually they wipe everything in the iframe and replace it with the content of the response.
 * There is no callback nor event to know when the iframe content is ready.
 * Thus we hook into the document.close method, to simulate this event.
 * @param formContainer
 * @param customCallback
 */
function hookIframeDocumentClose(
  formContainer: HTMLDivElement,
  customCallback: () => void,
) {
  const iframe = formContainer?.firstElementChild as HTMLIFrameElement;
  if (!iframe) {
    rollbar.warn('hookIframeDocumentClose iframe is null');
    return;
  }

  const contentDocument =
    iframe.contentDocument || iframe.contentWindow?.document;
  if (!contentDocument) {
    rollbar.warn('hookIframeDocumentClose iframe content document is null');
    return;
  }

  const originalClose = contentDocument.close;
  contentDocument.close = function close() {
    originalClose.apply(this);
    customCallback();
  };
}

/**
 * This component assumes that the script from "${env[PROJECT_SERVICE_AVALARA_EXEMPTIONS_API_URL}/js/gencert.js" has already been loaded and run
 */
export const AvalaraEcmPlugin = ({
  token,
  state,
  onSuccess,
}: AvalaraEcmPluginProps) => {
  const [formContainer, setFormContainer] = useState<HTMLDivElement | null>(
    null,
  );
  const [isAvalaraInitError, setIsAvalaraInitError] = useState(false);
  const [isAvalaraInitLoading, setIsAvalaraInitLoading] = useState(false);
  const customStylesEnabled = useFeatureFlagEnabled(
    'embedded_tax_exemptions___custom_styles',
  );

  const [alert, setAlert] = useState<
    { message: AlertProps['children']; color: AlertProps['color'] } | undefined
  >(undefined);

  if (state.length <= 2) {
    rollbar.warn('AvalaraEcmPlugin: state is not a full name', state);
  }

  useEffect(() => {
    if (formContainer && token && state) {
      try {
        setIsAvalaraInitLoading(true);
        window.GenCert.init(formContainer, {
          ship_zone: state,
          gencertAPI: avalaraApiUrl,
          token,
          primary_color: designTokens.color.primary.default.value,
          secondary_color: designTokens.color.secondary.default.value,
          afterShow: () => {
            setIsAvalaraInitLoading(false);
            if (customStylesEnabled) {
              hookIframeDocumentClose(formContainer, () =>
                applyCustomStyles(formContainer),
              );
            }
          },
          onCertSuccess: () => {
            if (!window.GenCert || !window.GenCert.certificate) {
              rollbar.error('GenCert or GenCert.certificate is undefined');
              return;
            }
            onSuccess(window.GenCert.certificate);
          },
          onUpload: () => {
            setAlert({
              color: 'success',
              message: 'The document has been uploaded.',
            });
          },
          onValidateFailure: () => {
            setAlert({
              color: 'error',
              message: 'There was an error with the validation.',
            });
          },
          onCertFailure: () => {
            setAlert({
              color: 'error',
              message: 'Certificate validation failed.',
            });
          },
          onNotNeeded: () => {
            setAlert({
              color: 'warning',
              message: "The selected zone doesn't charge sales tax",
            });
          },
        });
        window.GenCert.show();
      } catch (error) {
        setIsAvalaraInitError(true);
      }
    }
  }, [formContainer, token, state]);

  // The form container will be accessed outside React. We need to render the same object for the lifecycle of this component.
  const renderFormContainer = useMemo(() => <div ref={setFormContainer} />, []);

  return (
    <>
      {alert && <Alert severity={alert.color}>{alert.message}</Alert>}
      {isAvalaraInitError && <ErrorLoadingAvalaraAlert />}
      {isAvalaraInitLoading && <div>Loading...</div>}
      {renderFormContainer}
    </>
  );
};
